// src/index.tsx
import { useCallback, useState, useLayoutEffect } from "react";
import {
  Text,
  Flex,
  Spacer,
  useBreakpointValue,
  useToast as useChakraToast
} from "@chakra-ui/react";
import { Info, Ok, BoxImportant, Delete } from "@airhelp/icons";
import { jsx, jsxs } from "react/jsx-runtime";
var TOAST_ICONS = {
  info: Info,
  success: Ok,
  warning: BoxImportant,
  error: BoxImportant
};
var TOAST_COLORS = {
  info: "primary.600",
  success: "success.600",
  warning: "warning.500",
  error: "secondary.500"
};
var TOAST_TEXT_COLORS = {
  info: "primary.100",
  success: "success.100",
  warning: "warning.100",
  error: "secondary.100"
};
var Toast = ({ variant, title, description, onClose }) => {
  const [isRtl, setIsRtl] = useState(() => document && document.documentElement.dir === "rtl");
  const Icon = TOAST_ICONS[variant] ?? TOAST_ICONS["info"];
  const color = TOAST_COLORS[variant] ?? TOAST_COLORS["info"];
  const textColor = TOAST_TEXT_COLORS[variant] ?? TOAST_TEXT_COLORS["info"];
  useLayoutEffect(() => {
    const observer = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        if (mutation.type === "attributes") {
          setIsRtl(mutation.target instanceof HTMLElement && mutation.target.dir === "rtl");
        }
      }
    });
    document && observer.observe(document.documentElement, {
      attributes: true
    });
    return () => {
      observer.disconnect();
    };
  }, []);
  return /* @__PURE__ */ jsxs(
    Flex,
    {
      px: 4,
      py: 3,
      borderRadius: "md",
      backgroundColor: color,
      alignItems: "center",
      _last: { mb: 4 },
      flexDirection: isRtl ? "row-reverse" : "row",
      "data-status": variant,
      "data-testId": "toast-container",
      children: [
        /* @__PURE__ */ jsx(Icon, { color: textColor, fontSize: "20px", alignSelf: "flex-start" }),
        /* @__PURE__ */ jsxs(Flex, { flexDirection: "column", w: "100%", children: [
          /* @__PURE__ */ jsx(Text, { mx: 3, fontSize: "sm", textColor, fontWeight: 500, children: title }),
          /* @__PURE__ */ jsx(Text, { mx: 3, fontSize: "xs", textColor, fontWeight: 400, children: description })
        ] }),
        /* @__PURE__ */ jsx(Spacer, {}),
        onClose && /* @__PURE__ */ jsx(Delete, { fontSize: "16px", color: textColor, onClick: onClose, alignSelf: "flex-start" })
      ]
    }
  );
};
function useToast() {
  const position = useBreakpointValue({ base: "bottom", sm: "top" }, { fallback: "top" }) || "top";
  const toast = useChakraToast();
  return useCallback(
    ({
      variant,
      title,
      isClosable,
      onCloseComplete,
      duration = 5e3,
      description
    }) => toast({
      variant,
      description,
      title,
      isClosable,
      onCloseComplete,
      duration,
      position,
      containerStyle: {
        mb: position === "top" ? -2 : 4,
        mt: position === "top" ? 4 : -2
      }
    }),
    [position, toast]
  );
}
var toastOptions = {
  defaultOptions: {
    render({ variant, title, isClosable, onClose, duration, description }) {
      return /* @__PURE__ */ jsx(
        Toast,
        {
          variant: variant ?? "info",
          title,
          onClose: isClosable ? onClose : void 0,
          duration,
          description
        }
      );
    }
  }
};
export {
  Toast,
  toastOptions,
  useToast
};
